import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, BookMarked, Copy, Trash2, Download } from "lucide-react";

// Generic name prompt used for "clone phase" and "save phase to library".
export function PhaseNameDialog({ action, onOpenChange, onConfirm }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (action) setName(action.type === "clone" ? `${action.phase} (copy)` : `${action.phase} template`);
  }, [action]);
  const isClone = action?.type === "clone";
  const go = async () => {
    if (!name.trim()) { toast.error("Please enter a name"); return; }
    setBusy(true);
    try { await onConfirm(name.trim()); onOpenChange(false); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!action} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="phase-name-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            {isClone ? <Copy className="h-5 w-5 text-primary" /> : <BookMarked className="h-5 w-5 text-primary" />}
            {isClone ? `Duplicate "${action?.phase}"` : `Save "${action?.phase}" to the Library`}
          </DialogTitle>
          <DialogDescription>
            {isClone ? "The phase and everything in it will be copied under the new name." : "The phase structure, durations and dependencies will be reusable in any project."}
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label htmlFor="phase-new-name">{isClone ? "New phase name" : "Template name"}</Label>
          <Input id="phase-new-name" autoFocus value={name} onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && go()} className="mt-1.5" data-testid="phase-name-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="phase-name-cancel">Cancel</Button>
          <Button onClick={go} disabled={busy} data-testid="phase-name-confirm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : isClone ? "Duplicate phase" : "Save to Library"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Pick a saved template and import it into the current project.
export function PhaseImportDialog({ open, onOpenChange, projectId, onImported }) {
  const [templates, setTemplates] = useState(null);
  const [busy, setBusy] = useState("");
  const [anchor, setAnchor] = useState(() => new Date().toISOString().slice(0, 10));

  useEffect(() => {
    if (open) api.get("/phase-templates").then(({ data }) => setTemplates(data)).catch(() => setTemplates([]));
  }, [open]);

  const importTpl = async (tpl) => {
    setBusy(tpl.id);
    try {
      const { data } = await api.post(`/projects/${projectId}/phases/import`, { template_id: tpl.id, anchor_date: anchor || null });
      toast.success(`Imported "${data.new_phase}" (${data.imported} item${data.imported === 1 ? "" : "s"})`);
      onImported?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed"); }
    finally { setBusy(""); }
  };
  const remove = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try { await api.delete(`/phase-templates/${tpl.id}`); setTemplates((ts) => ts.filter((t) => t.id !== tpl.id)); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="phase-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2"><BookMarked className="h-5 w-5 text-primary" /> Import a phase from the Library</DialogTitle>
          <DialogDescription>Saved phase templates keep their structure, durations and dependencies. Dates are re-anchored to the start date below.</DialogDescription>
        </DialogHeader>
        <div>
          <Label htmlFor="phase-anchor">Phase start date</Label>
          <Input id="phase-anchor" type="date" value={anchor} onChange={(e) => setAnchor(e.target.value)} className="mt-1.5 w-44" data-testid="phase-import-anchor" />
        </div>
        {!templates ? (
          <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : templates.length === 0 ? (
          <p className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-6 text-center" data-testid="phase-templates-empty">
            No saved phases yet. Use the <BookMarked className="inline h-3.5 w-3.5" /> icon on any WBS phase header to save one to the Library.
          </p>
        ) : (
          <div className="space-y-2">
            {templates.map((tpl) => (
              <div key={tpl.id} className="flex items-center gap-3 border border-border rounded-lg p-3" data-testid={`phase-template-${tpl.id}`}>
                <span className="h-3 w-3 rounded-full shrink-0" style={{ background: tpl.color || "#0044FF" }} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">{tpl.name}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{tpl.task_count} item{tpl.task_count === 1 ? "" : "s"} · from {tpl.source_project} / {tpl.source_phase} · {String(tpl.created_at).slice(0, 10)}</p>
                </div>
                <Button size="sm" className="h-7 text-xs gap-1 shrink-0" disabled={busy === tpl.id} onClick={() => importTpl(tpl)} data-testid={`phase-template-import-${tpl.id}`}>
                  {busy === tpl.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />} Import
                </Button>
                <button onClick={() => remove(tpl)} className="p-1.5 rounded hover:bg-red-50 text-muted-foreground hover:text-red-600" title="Delete template" data-testid={`phase-template-delete-${tpl.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
